import { GoogleLogin } from '@react-oauth/google'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { Info } from 'lucide-react'
import { useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { WretchError } from 'wretch/resolver'

import { Button } from '@/components/button/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/card/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/form/form'
import { Input } from '@/components/input/input'

import { LOCAL_STORAGE_KEY_MAP } from '@/lib/constants/platform'
import { useResizeElement } from '@/lib/hooks/use-resize-element'
import { signInGoogleMutation } from '@/lib/queries/business/v1/auth/login/google/mutations'
import { signInMutation } from '@/lib/queries/business/v1/auth/login/mutations'
import { signUpMutation } from '@/lib/queries/business/v1/auth/signup/mutations'
import { getUserQuery } from '@/lib/queries/business/v2/user/queries'
import { cn } from '@/lib/utils'

export const SignUpForm = () => {
  const search = useSearch({
    from: '/sign-up',
  })
  const navigate = useNavigate()
  const mutation = useMutation(signUpMutation)
  const mutationGoogle = useMutation(signInGoogleMutation)
  const mutationSignIn = useMutation(signInMutation)
  const form = useForm<{
    email: string
    password: string
  }>({
    defaultValues: { email: '', password: '' },
  })
  const { data: dataUser, refetch } = useQuery(getUserQuery)

  const { elementRef: cardContentWrapperRef, size: cardContentWrapperSize } = useResizeElement<HTMLDivElement>()

  const handleSubmit = form.handleSubmit(({ email, password }) => {
    mutation.mutate(
      { email, password },
      {
        onSuccess: () => {
          signIn({ email, password })
        },
        onError: (err) => {
          if (err instanceof WretchError) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (err.json.code === 'AlreadyExistUser') {
              form.setError('email', {
                message: 'This email address is already registered. Please log in.',
              })
            }
          }
        },
      }
    )
  })

  const signIn = useCallback(
    (credential: { email: string; password: string }) => {
      mutationSignIn.mutate(credential, {
        onSuccess: async (data) => {
          localStorage.setItem(LOCAL_STORAGE_KEY_MAP.token, data.token)
          await refetch()
          await navigate({ to: search.redirect ?? '/' })
        },
        onError: async () => {
          await navigate({ to: '/sign-in' })
        },
      })
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mutationSignIn, navigate, refetch]
  )

  const { errors } = form.formState

  return (
    <>
      <Form {...form}>
        <form className={cn('flex flex-col space-y-6', 'w-full')} onSubmit={handleSubmit}>
          <Card className={cn('mx-auto', 'w-full max-w-[400px]', 'border-none', 'shadow-none')}>
            <CardHeader className={cn('px-0')}>
              <CardTitle className="leading-8">Create account</CardTitle>
              <CardDescription>Enter your email below to create your account</CardDescription>
            </CardHeader>

            <CardContent className={cn('px-0')}>
              <div ref={cardContentWrapperRef} className={cn('flex flex-col gap-4', 'w-full')}>
                <GoogleLogin
                  width={cardContentWrapperSize?.width ?? 400}
                  text="continue_with"
                  onSuccess={(credentialResponse) => {
                    mutationGoogle.mutate(
                      {
                        clientId: credentialResponse.clientId,
                        token: credentialResponse.credential,
                      },
                      {
                        onSuccess: async (data) => {
                          localStorage.setItem(LOCAL_STORAGE_KEY_MAP.token, data.token)
                          await refetch()
                          await navigate({ to: search.redirect ?? '/' })
                        },
                        onError: () => {
                          console.log('Login Failed')
                        },
                      }
                    )
                  }}
                  onError={() => {
                    console.log('Login Failed')
                  }}
                />
                <div className="flex w-full items-center">
                  <div className="h-[1px] flex-1 bg-zinc-200" />
                  <div className="p-2.5 text-xs text-muted-foreground">OR CONTINUE WITH</div>
                  <div className="h-[1px] flex-1 bg-border" />
                </div>
                {/* Input: email */}
                <FormField
                  control={form.control}
                  rules={{ required: true }}
                  name="email"
                  disabled={!!dataUser}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          autoFocus
                          className={cn('placeholder:text-faint-foreground', !!errors.email && 'border-red-500')}
                          placeholder="email@example.com"
                          {...field}
                        />
                      </FormControl>
                      {!!errors.email && (
                        <div className="flex items-center gap-1">
                          <Info className="text-red-500" size={12} />
                          <FormMessage className="text-xs" />
                        </div>
                      )}
                    </FormItem>
                  )}
                />
                {/* Input: password */}
                <FormField
                  control={form.control}
                  rules={{ required: true }}
                  name="password"
                  disabled={!!dataUser}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <Input className="placeholder:text-faint-foreground" type="password" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>

            <CardFooter className={cn('px-0 pb-12')}>
              {/* Submit */}
              <Button
                className="w-full"
                type="submit"
                size="sm"
                variant="default"
                disabled={mutation.isPending || !!dataUser || !!errors.email || !!errors.password}
              >
                Create account
              </Button>
            </CardFooter>
          </Card>
        </form>
      </Form>

      <div className={cn('mx-auto', 'max-w-[250px]', 'text-center text-[10px] leading-[16px] text-muted-foreground')}>
        By continuing, you agree to our
        <br />
        <Link className="border-b border-faint-foreground" to="/terms-of-service">
          Terms of Service
        </Link>{' '}
        and{' '}
        <Link className="border-b border-faint-foreground" to="/privacy-policy">
          Privacy Policy
        </Link>
        .
      </div>

      <div className={cn('mx-auto', 'text-xs', 'mt-6')}>
        Already have an account?{' '}
        <Link className="text-primary" to="/sign-in">
          Log in
        </Link>
      </div>
    </>
  )
}
